/**
 * Signature API — user-facing endpoints for uploading, listing, previewing,
 * and deleting signature images.
 *
 * Base: /api/applications/:applicationId/signatures
 */
import apiClient, { fetchWithSessionRefresh } from './index';
import API_BASE_URL from './API_BASE_URL';
import { csrfHeaders } from '../utils/csrf';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SignatureInfo {
  id: number;
  applicationId: number;
  role: string;
  mimeType: string;
  fileSize: number;
  originalFileName: string | null;
  isActive: boolean;
  uploadedBy: number | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const base = (applicationId: number) =>
  `/applications/${applicationId}/signatures`;

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * List all (non-deleted) signatures for an application.
 */
export const listSignatures = async (
  applicationId: number
): Promise<SignatureInfo[]> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data: { items: SignatureInfo[] };
    }>(base(applicationId));
    return response.data.data?.items ?? [];
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || 'Failed to load signatures';
    throw new Error(message);
  }
};

/**
 * Upload a new signature image for a given role.
 *
 * Uses native fetch instead of axiosInstance because the axios default
 * Content-Type: application/json conflicts with multipart/form-data.
 * With fetch we omit Content-Type entirely and let the browser set the
 * correct "multipart/form-data; boundary=..." header automatically.
 */
export const uploadSignature = async (
  applicationId: number,
  role: string,
  file: File
): Promise<SignatureInfo> => {
  const formData = new FormData();
  formData.append('signature', file);
  formData.append('role', role);

  // Task 149/150: authenticated by the HttpOnly cookie that `credentials:
  // 'include'` below sends. This is a POST, so it carries the CSRF token.
  // Intentionally NO Content-Type — browser sets multipart boundary automatically.
  // Built per attempt so a retry after a session refresh carries the new
  // CSRF token.
  const res = await fetchWithSessionRefresh(`${API_BASE_URL}${base(applicationId)}`, () => ({
    method: 'POST',
    headers: { ...csrfHeaders() },
    body: formData,
    credentials: 'include',
  }));

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      (json as any)?.message ||
      (json as any)?.error?.message ||
      `Upload failed (${res.status})`
    );
  }

  return (json as any).data as SignatureInfo;
};

/**
 * Get a short-lived preview URL (15 min) for a signature image.
 */
export const getSignaturePreviewUrl = async (
  applicationId: number,
  signatureId: number
): Promise<string> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data: { url: string };
    }>(`${base(applicationId)}/${signatureId}/preview`);
    return response.data.data?.url ?? '';
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || 'Failed to get preview URL';
    throw new Error(message);
  }
};

/**
 * Soft-delete a signature.
 */
export const deleteSignature = async (
  applicationId: number,
  signatureId: number
): Promise<void> => {
  try {
    await apiClient.delete(`${base(applicationId)}/${signatureId}`);
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || 'Failed to delete signature';
    throw new Error(message);
  }
};
